import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Rect, Stage } from 'react-konva'
import useDebounce from '../../../hooks/useDebounce'

import { useSelectedVariantContext } from '../context/SelectedVariantContext'
import {
    actionChangeSelectedLayer,
    actionCreateStageRefBySide,
    actionRerenderState,
    actionToggleCropMode,
    actionUpdateMultiSelectionLayers,
} from '../context/SelectedVariantContext/action'
import MockupLayers from './layers'
import Konva from 'konva'
import { updateToolbarStyle } from '../context/SelectedVariantContext/helper'
import { getStoreSetting } from '../../../services/CurrentStoreServices'

const MockupStage = ({ stage, isActive, isOptionalSide, cropMode }) => {
    const {
        state: { editorWrapper, stageDrag, selectedLayer, productCreatedType, multiSelectionLayers, disabledMultiSelection },
        dispatch,
    } = useSelectedVariantContext()
    const layerRef = useRef(null)

    const { background, layers } = stage
    const ratioDefault = background?.ratioDefault

    // Debounce layers to avoid excessive updates
    const debouncedLayers = useDebounce(layers, 1)

    const [selection, setSelection] = useState({
        visible: false,
    })
    const [selectedIds, setSelectedIds] = useState([])
    const [allowSelect, setAllowSelect] = useState(false)

    const isSelecting = useRef(false)
    const stageRef = useRef(null)
    const backgroundRef = useRef(null)
    const rectRefs = useRef(new Map())
    const transformerRef = useRef(null)

    useEffect(() => {
        const stage = stageRef.current
        const container = stage.container()
        const canvas = container.querySelector('canvas')
        if (canvas) {
            canvas.setAttribute('id', 'test-canvas')
        }
    }, [])

    useEffect(() => {
        if (isOptionalSide) return
        if (stageRef.current && stage.side) {
            dispatch(
                actionCreateStageRefBySide({
                    stageRef: stageRef.current,
                    side: stage.side,
                    attributeIds: stage.attributeIds,
                })
            )
        }
    }, [isOptionalSide, dispatch, stage])

    useEffect(() => {
        if (selectedIds.length && transformerRef.current) {
            // Get the nodes from the refs Map
            const nodes = selectedIds
                .map((id) => {
                    const node = rectRefs.current.get(id)
                    return node
                })
                .filter((node) => node)
            if (nodes.length > 0) {
                transformerRef.current.nodes(nodes)
                transformerRef.current.getLayer().batchDraw()
                transformerRef.current.moveToTop()
            } else {
                transformerRef.current.nodes([])
            }
        } else if (transformerRef.current) {
            // Clear selection
            transformerRef.current.nodes([])
        }
        if (transformerRef.current && allowSelect) {
            const transformerRect = transformerRef?.current?.children[0]?.attrs
            const currentScaleX = stageRef?.current?.scaleX()
            const currentScaleY = stageRef?.current?.scaleY()
            const originalTransformerRect = {
                width: transformerRect.width / currentScaleX,
                height: transformerRect.height / currentScaleY,
            }
            setAllowSelect(false)
            dispatch(
                actionUpdateMultiSelectionLayers({
                    multiSelectionLayers: layers.filter((layer) => selectedIds.includes(layer.id)),
                    originalTransformerRect,
                })
            )
        }
    }, [selectedIds])

    useEffect(() => {
        if (!selectedIds.length) return
        if (!multiSelectionLayers?.length && selectedLayer) {
            setSelectedIds([])
            return
        }
        const newSelectedLayers = debouncedLayers.filter((layer) => selectedIds.includes(layer.id))
        setSelectedIds(newSelectedLayers.map((layer) => layer.id))
        setAllowSelect(true)
    }, [debouncedLayers, multiSelectionLayers?.length])

    useEffect(() => {
        if (multiSelectionLayers?.length > 0) {
            updateToolbarStyle({}, 'image-toolbar-multi-selection')
        }
    }, [multiSelectionLayers])

    /**
     * Detaches the currently selected layer if the clicked position is empty
     * @param {MouseEvent} e - The mouse event
     */
    const onDeAttach = (e) => {
        const clickedOnEmpty = e.target === e.target.getStage()
        if (clickedOnEmpty) {
            dispatch(actionChangeSelectedLayer(null))
            dispatch(actionToggleCropMode(false))
            // Clear multi-selection when clicking on empty space
            setSelectedIds([])
        }
    }

    /**
     * Zoom in or out based on mouse wheel event
     * @param {MouseEvent} e - The mouse event
     */
    const onZoom = (e) => {
        if (
            !ratioDefault ||
            isOptionalSide ||
            layers.length === 0 ||
            productCreatedType === 'regularNoTemplate' ||
            cropMode
        )
            return

        const stage = stageRef.current
        // Get the amount of wheel movement
        const { deltaY } = e.evt

        // Get the current position of the mouse pointer on the stage
        const pointer = stage.getPointerPosition()

        const scaleFactor = deltaY < 0 ? 1.1 : 1 / 1.1

        // Calculate the new scale
        const newScaleX = stage.scaleX() * scaleFactor
        const newScaleY = stage.scaleY() * scaleFactor

        // Define scale boundaries
        const minScale = ratioDefault / 5
        const maxScale = ratioDefault * 10

        // Check if new scale is within boundaries
        if (newScaleX < minScale || newScaleX > maxScale || newScaleY < minScale || newScaleY > maxScale) {
            return
        }

        // Calculate the new position of the stage based on the current position of the mouse pointer
        const newX = pointer.x - (pointer.x - stage.x()) * scaleFactor
        const newY = pointer.y - (pointer.y - stage.y()) * scaleFactor

        stage.scale({ x: newScaleX, y: newScaleY })
        stage.position({ x: newX, y: newY })
        stage.batchDraw()
        dispatch(actionRerenderState())
    }

    const isAnableSelect = useMemo(() => {
        return productCreatedType === 'regularNoTemplate'
    }, [productCreatedType])

    const handleMouseDown = (e) => {
        // Do nothing if we mousedown on any shape

        // Don't start selection if stage is draggable
        if (stageDrag || cropMode || !layers.length || isAnableSelect || disabledMultiSelection) {
            return
        }

        // Check if click is within background bounds
        if (background) {
            const stage = e.target.getStage()
            const { x: pointerX, y: pointerY } = stage.getPointerPosition()

            // Calculate position considering scale (zoom)
            const scaleX = stage.scaleX()
            const scaleY = stage.scaleY()
            const pos = {
                x: (pointerX - stage.x()) / scaleX,
                y: (pointerY - stage.y()) / scaleY,
            }

            // Check if click is within background bounds
            const { x: bgX, y: bgY, width: bgWidth, height: bgHeight } = background
            const isWithinBackground = pos.x >= bgX && pos.x <= bgX + bgWidth && pos.y >= bgY && pos.y <= bgY + bgHeight

            // If click is within background bounds and not on stage, don't start selection
            if (isWithinBackground && e.target !== e.target.getStage()) {
                setSelectedIds([])
                return
            }
            if (!isWithinBackground && e.target?.attrs?.hasPattern && !e.target?.attrs?.isPattern) {
                setSelectedIds([])
                return
            }
            if (!isWithinBackground && !e.target?.attrs?.hasPattern && e.target !== e.target.getStage()) {
                setSelectedIds([])
                return
            }
        }
        setAllowSelect(true)
        // Start selection rectangle
        isSelecting.current = true
        const stage = e.target.getStage()
        const { x: pointerX, y: pointerY } = stage.getPointerPosition()

        // Calculate position considering scale (zoom)
        const scaleX = stage.scaleX()
        const scaleY = stage.scaleY()
        const pos = {
            x: (pointerX - stage.x()) / scaleX,
            y: (pointerY - stage.y()) / scaleY,
        }

        // Validate position
        if (isNaN(pos.x) || isNaN(pos.y)) {
            console.warn('Invalid mouse position:', { pointerX, pointerY, scaleX, scaleY, pos })
            return
        }

        setSelection({
            visible: true,
            startX: pos.x,
            startY: pos.y,
            endX: pos.x,
            endY: pos.y,
            x: pos.x,
            y: pos.y,
            width: 0,
            height: 0,
        })
    }

    const handleMouseMove = (e) => {
        // Do nothing if we didn't start selection
        if (!isSelecting.current) {
            return
        }

        const stage = e.target.getStage()
        const { x: pointerX, y: pointerY } = stage.getPointerPosition()

        // Calculate position considering scale (zoom)
        const scaleX = stage.scaleX()
        const scaleY = stage.scaleY()
        const pos = {
            x: (pointerX - stage.x()) / scaleX,
            y: (pointerY - stage.y()) / scaleY,
        }

        // Tính toán rõ ràng
        const startX = selection.startX
        const startY = selection.startY
        const endX = pos.x
        const endY = pos.y

        // Proper bounds calculation for any drag direction
        const rectX = Math.min(startX, endX)
        const rectY = Math.min(startY, endY)
        const rectWidth = Math.abs(endX - startX)
        const rectHeight = Math.abs(endY - startY)

        // Ensure minimum selection size
        const minSize = 5

        // Validate coordinates
        if (isNaN(rectX) || isNaN(rectY) || isNaN(rectWidth) || isNaN(rectHeight)) {
            console.warn('Invalid selection coordinates:', {
                startX,
                startY,
                endX,
                endY,
                rectX,
                rectY,
                rectWidth,
                rectHeight,
            })
            return
        }

        setSelection({
            ...selection,
            endX: pos.x,
            endY: pos.y,
            x: rectX,
            y: rectY,
            width: Math.max(rectWidth, minSize),
            height: Math.max(rectHeight, minSize),
        })
    }

    const handleMouseUp = () => {
        // Do nothing if we didn't start selection
        if (!isSelecting.current) {
            return
        }
        isSelecting.current = false

        // Update visibility immediately
        setSelection({
            ...selection,
            visible: false,
        })

        const selBox = {
            x: selection.x,
            y: selection.y,
            width: selection.width,
            height: selection.height,
        }

        const selected = layers.filter((layer) => {
            // Use layer data directly instead of rectRefs

            // Calculate bounds directly from layer attributes
            const { x = 0, y = 0, width = 0, height = 0, offsetX = 0, offsetY = 0, scaleX = 1, scaleY = 1 } = layer

            // Calculate actual dimensions with scale
            const actualWidth = width * Math.abs(scaleX)
            const actualHeight = height * Math.abs(scaleY)

            // Calculate top-left position considering offset
            const topLeftX = x - offsetX * Math.abs(scaleX)
            const topLeftY = y - offsetY * Math.abs(scaleY)

            const layerBounds = {
                x: topLeftX,
                y: topLeftY,
                width: actualWidth,
                height: actualHeight,
            }

            // Check if rectangle intersects with selection box
            const intersects = Konva.Util.haveIntersection(selBox, layerBounds)

            return intersects
        })

        setSelectedIds(selected.map((layer) => layer.id))
    }

    // Add global mouse event listeners to handle mouse events outside the stage
    useEffect(() => {
        const handleGlobalMouseMove = (e) => {
            // Only handle if we're currently selecting
            if (isSelecting.current) {
                handleMouseMove({ target: stageRef.current })
            }
        }

        const handleGlobalMouseUp = (e) => {
            // Only handle if we're currently selecting
            if (isSelecting.current) {
                handleMouseUp()
            }
        }

        // Add global event listeners
        document.addEventListener('mousemove', handleGlobalMouseMove)
        document.addEventListener('mouseup', handleGlobalMouseUp)

        // Cleanup function
        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove)
            document.removeEventListener('mouseup', handleGlobalMouseUp)
        }
    }, [isSelecting.current, selection])

    return (
        <Stage
            ref={stageRef}
            width={editorWrapper.width}
            height={editorWrapper.height}
            scaleX={ratioDefault}
            scaleY={ratioDefault}
            onClick={onDeAttach}
            draggable={stageDrag}
            onWheel={onZoom}
            style={{
                display: isActive ? 'block' : 'none',
                visibility: isActive ? 'visible' : 'hidden'
            }}
            onMouseEnter={(e) => {
                const container = e.target.getStage().container()
                container.style.cursor = stageDrag ? 'pointer' : 'default'
            }}
            onMouseDown={(e) => {
                const container = e.target.getStage().container()
                container.style.cursor = stageDrag ? 'grabbing' : 'default'
                handleMouseDown(e)
            }}
            onMouseUp={(e) => {
                const container = e.target.getStage().container()
                container.style.cursor = stageDrag ? 'pointer' : 'default'
                handleMouseUp(e)
            }}
            onMousemove={handleMouseMove}
            onMouseLeave={(e) => {
                const container = e.target.getStage().container()
                container.style.cursor = 'default'
            }}
        >
            {background && (
                <MockupLayers
                    background={background}
                    layers={layers}
                    selectedLayer={selectedLayer}
                    dispatch={dispatch}
                    draggable={stageDrag}
                    selection={selection}
                    rectRefs={rectRefs}
                    selectedIds={selectedIds}
                    transformerRef={transformerRef}
                    cropMode={cropMode}
                    editorWrapper={editorWrapper}
                    productCreatedType={productCreatedType}
                />
            )}
        </Stage>
    )
}

export default MockupStage

